namespace $ {

	export type $shm_ba_data_load_stage = 'cache' | 'download' | 'save' | 'prepare'
	export type $shm_ba_data_load_state = {
		stage: $shm_ba_data_load_stage
		loaded: number
		total: number
	}
	type $shm_ba_data_load_transfer = {
		bytes: Uint8Array< ArrayBuffer > | null
		chunks: Uint8Array[]
		loaded: number
		direct_loaded: number
		index: number
	}

	const $shm_ba_data_loader_updated = new WeakMap< $shm_ba_data_loader, number >()
	const $shm_ba_data_loader_started = new WeakSet< $shm_ba_data_loader >()
	const $shm_ba_data_loader_transfers = new WeakMap< $shm_ba_data_loader, $shm_ba_data_load_transfer >()

	export class $shm_ba_data_loader extends $mol_object2 {

		uri() { return '' }

		@ $mol_mem
		state( next?: $shm_ba_data_load_state ) {
			return next ?? { stage: 'cache', loaded: 0, total: 0 }
		}

		@ $mol_action
		state_set( next: $shm_ba_data_load_state ) {
			this.state( next )
		}

		@ $mol_action
		download_start() {
			if( $shm_ba_data_loader_started.has( this ) ) return
			$shm_ba_data_loader_started.add( this )
			this.state_set({ stage: 'download', loaded: 0, total: 0 })
		}

		@ $mol_action
		download_progress( loaded: number, total: number ) {
			const previous = this.state()
			if( previous.stage !== 'download' ) return
			if( loaded <= previous.loaded && total === previous.total ) return
			this.state_set({ stage: 'download', loaded, total })
		}

		@ $mol_mem
		response() {
			return $mol_fetch.success( this.uri() )
		}

		@ $mol_mem
		reader() {
			return this.response().stream()?.getReader() ?? null
		}

		async read_async( reader: ReadableStreamDefaultReader< Uint8Array >, index: number ) {
			return reader.read()
		}

		@ $mol_mem_key
		read( index: number ) {
			return $mol_wire_sync( this ).read_async( this.reader()!, index )
		}

		@ $mol_mem
		buffer() {
			this.download_start()
			const response = this.response()
			const content_length = Number( response.headers().get( 'Content-Length' ) || 0 )
			const total = Number.isFinite( content_length ) && content_length > 0 ? content_length : 0
			const reader = this.reader()
			if( !reader ) {
				const buffer = response.buffer()
				this.download_progress( buffer.byteLength, total )
				return buffer
			}

			let transfer = $shm_ba_data_loader_transfers.get( this )
			if( !transfer ) {
				transfer = {
					bytes: total ? new Uint8Array( total ) : null,
					chunks: [],
					loaded: 0,
					direct_loaded: 0,
					index: 0,
				}
				$shm_ba_data_loader_transfers.set( this, transfer )
			}

			for( ; ; ) {
				const part = this.read( transfer.index )
				if( part.done ) break
				if( transfer.bytes && transfer.loaded + part.value.byteLength <= transfer.bytes.byteLength ) {
					transfer.bytes.set( part.value, transfer.loaded )
					transfer.direct_loaded += part.value.byteLength
				} else {
					transfer.chunks.push( part.value )
				}
				transfer.loaded += part.value.byteLength
				++ transfer.index

				const now = performance.now()
				const updated = $shm_ba_data_loader_updated.get( this ) ?? 0
				if( now - updated >= 100 || transfer.loaded === total ) {
					this.download_progress( transfer.loaded, total )
					$shm_ba_data_loader_updated.set( this, now )
				}
			}

			if( transfer.bytes && transfer.direct_loaded === transfer.bytes.byteLength && !transfer.chunks.length ) {
				this.download_progress( transfer.loaded, total )
				return transfer.bytes.buffer
			}

			const result = new Uint8Array( transfer.loaded )
			let offset = 0
			if( transfer.bytes ) {
				result.set( transfer.bytes.subarray( 0, transfer.direct_loaded ), offset )
				offset += transfer.direct_loaded
			}
			for( const chunk of transfer.chunks ) {
				result.set( chunk, offset )
				offset += chunk.byteLength
			}
			this.download_progress( transfer.loaded, total )
			return result.buffer
		}

	}

}
